import { type FileWithPath } from 'react-dropzone';
import { FileIcon, ImageIcon, type LucideIcon, VideoIcon } from 'lucide-react';
import PromisePool from '@supercharge/promise-pool';
import { throttle } from 'lodash';
import { create } from 'zustand';
import { ulid } from 'ulid';
import {
  TaskState,
  type UploadAsset,
  UploadFinishedPercentage,
  UploadFinishingPercentage,
  type UploadStatus,
} from '@/types/uploads';
import { capitalizeFileExtension, getUploadCsvKey } from '@/lib/utils';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { DAMNodeType, NodeType } from '@/types/tree';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { Variants } from '@/types/asset';

const throttledRefetch = throttle((refetchFn: () => void) => {
  refetchFn();
}, 10000);

const UPLOAD_RATE_LIMIT_MAX_RETRIES = 5;
const UPLOAD_RATE_LIMIT_BASE_DELAY_MS = 60 * 1000; // 1 minute

const isUploadRateLimitError = (err: unknown): boolean =>
  (err as any)?.response?.status === 429 || (err as any)?.status === 429;

/** Thrown only for a linkId-scoped claim (see createAsset) — a contributor's grant died. */
const isUploadLinkDeadError = (err: unknown): boolean =>
  (err as any)?.response?.status === 403 || (err as any)?.status === 403;

/**
 * Whether a queued file actually made it. On success `updateFileWithRetry` re-keys the
 * progress entry from the temporary file id to the real asset id and rewrites the id inside
 * `filesByFolder`, so `assetsUploadProgress[file.id]` is *absent* for a file that succeeded
 * and present for one still going. Checking `.uploaded` under the temporary id therefore
 * reports every finished file as unfinished (DM-2533).
 */
export const createEmptyUploadSession = (active: boolean): UploadSession => ({
  uploadFolderPath: '',
  selectedAlbums: [],
  filesByFolder: {},
  filesData: [],
  totalAssetsToUpload: 0,
  uploadState: TaskState.NotStarted,
  assetsUploadProgress: {},
  erroredAssets: {},
  averageProgress: 0,
  errors: [],
  selectedAssets: [],
  uploadStatus: 'ready',
  keepExistingFolders: true,
  replaceAssetName: false,
  isAssetsProcessingCanceled: false,
  assetsLoading: false,
  csvFileName: '',
  csvData: {},
  active,
  completedUploadCount: 0,
  uploadFolderId: '',
  linkId: undefined,
  linkNoLongerAvailable: false,
  manualMetadata: {},
  refetchNode: undefined,
  isAllSelected: false,
  currentSelectedType: NodeType.Folders,
  versionUploadAssetId: undefined,
  versionUploadVersionId: undefined,
  isVersionUpload: false,
});

export const isUploadFileSaved = (session: UploadSession, fileId: string): boolean => {
  if (session.erroredAssets?.[fileId]) {
    return false;
  }
  const stillTrackedUnderTempId =
    Boolean(session.assetsUploadProgress[fileId]) ||
    Object.values(session.filesByFolder ?? {}).some((files) => files.some((file) => file.id === fileId));
  return !stillTrackedUnderTempId;
};

/**
 * The per-file claim goes through `getAccessTokenSilently`, so when the session
 * dies mid-batch it is this call that throws. That single failure both ends the
 * batch and clears auth0-react's user, and the batch ends first — so the wall
 * cannot infer "auth was lost during an upload" from `uploading`, which is
 * already false by then. Flagging it here is what makes the wall's decision
 * causal rather than a race (DM-2533).
 *
 * Error shapes match `shouldSuppressGlobalErrorToast` in `lib/queryErrorToast.ts`.
 */
const isUploadAuthError = (err: unknown): boolean => {
  const e = err as any;
  return (
    e?.error === 'login_required' ||
    e?.error === 'consent_required' ||
    e?.error === 'missing_refresh_token' ||
    e?.status === 401 ||
    e?.response?.status === 401
  );
};

/**
 * Waits for `delayMs` milliseconds, polling every second to check whether the
 * upload session has been cancelled.  Returns `true` if the full delay elapsed
 * and `false` if the session was cancelled (or removed) during the wait.
 */
const rateLimitDelay = async (delayMs: number, sessionId: string): Promise<boolean> => {
  const pollInterval = 1000;
  let elapsed = 0;
  while (elapsed < delayMs) {
    await new Promise<void>((resolve) => setTimeout(resolve, Math.min(pollInterval, delayMs - elapsed)));
    elapsed += pollInterval;
    const session = useUploadStore.getState().uploads[sessionId];
    if (!session || session.uploadState === TaskState.Cancelled) {
      return false;
    }
  }
  return true;
};

const joinFolderPath = (base: string, child: string) => {
  const trimmedBase = base.replace(/\/+$/g, '');
  const trimmedChild = child.replace(/^\/+/g, '');

  if (!trimmedBase) {
    return trimmedChild ? `/${trimmedChild}` : '';
  }

  if (!trimmedChild) {
    return trimmedBase;
  }

  return `${trimmedBase}/${trimmedChild}`;
};

/**
 * Uploads are not restricted by type. Maps recognized mime types to extensions so a blank
 * or generic type resolves when its extension is listed; otherwise inferMimeType returns
 * undefined instead of guessing.
 */
const MIME_TYPE_EXTENSIONS: Record<string, Array<string>> = {
  'image/png': ['.png'],
  'image/x-png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg', '.jpeg'],
  'image/pjpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/tiff': ['.tiff', '.tif'],
  'image/vnd.adobe.photoshop': ['.psd'],
  'image/x-sony-arw': ['.arw'],
  'image/x-canon-cr2': ['.cr2'],
  'image/x-canon-crw': ['.crw'],
  'image/x-kodak-dcr': ['.dcr'],
  'image/x-adobe-dng': ['.dng'],
  'image/x-epson-erf': ['.erf'],
  'image/x-kodak-k25': ['.k25'],
  'image/x-kodak-kdc': ['.kdc'],
  'image/x-minolta-mrw': ['.mrw'],
  'image/x-nikon-nef': ['.nef'],
  'image/x-olympus-orf': ['.orf'],
  'image/x-pentax-pef': ['.pef'],
  'image/x-fuji-raf': ['.raf'],
  'image/x-panasonic-raw': ['.raw', '.rw2'],
  'image/x-sony-sr2': ['.sr2'],
  'image/x-sony-srf': ['.srf'],
  'image/x-sigma-x3f': ['.x3f'],
  'image/x-hasselblad-3fr': ['.3fr'],
  'image/x-portable-pixmap': ['.ppm'],
  'image/x-minolta-raw': ['.mos'],
  'image/x-leica-raw': ['.dng'],
  'image/x-mamiya-mef': ['.mef'],
  'image/x-minolta-mdc': ['.mdc'],
  'image/x-nikon-nrw': ['.nrw'],
  'image/x-samsung-srw': ['.srw'],
  'image/x-canon-cr3': ['.cr3'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif', '.hif'],
  'application/postscript': ['.ai'],
  'image/x-eps': ['.eps'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'text/xml': ['.xml'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/rtf': ['.rtf'],
  'application/xml': ['.xml'],
  'application/zip': ['.zip'],
  'application/gzip': ['.gz'],
  'text/markdown': ['.md'],
  'video/mp4': ['.mp4', '.m4v'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
  'video/x-matroska': ['.mkv'],
  'video/webm': ['.webm'],
  'application/mxf': ['.mxf'],
};

const mimeSubtypeToExtension = Object.fromEntries(
  Object.entries(MIME_TYPE_EXTENSIONS)
    .map(([mime, extensions]) => {
      const subtype = mime.split('/')[1];
      const extension = extensions[0]?.replace(/^\./, '');
      return subtype && extension ? ([subtype, extension] as [string, string]) : null;
    })
    .filter((entry): entry is [string, string] => Boolean(entry)),
);

// Mimetypes shared by more than one real format; `application/postscript` covers both .ai and .eps.
const AMBIGUOUS_MIME_SUBTYPES = new Set(['postscript']);

/**
 * `fileName` only wins over `value` when there's no mimetype, or the mimetype is ambiguous.
 * Otherwise the backend-detected mimetype is authoritative.
 */
export const formatTypeLabel = (value?: string | null, fileName?: string | null) => {
  const nameExtension = fileName?.includes('.') ? fileName.split('.').pop()?.toLowerCase() : undefined;

  const raw = value?.includes('/') ? (value.split('/').pop() ?? value) : value;
  const normalized = raw?.toLowerCase();

  const preferExtension = !value || (normalized !== undefined && AMBIGUOUS_MIME_SUBTYPES.has(normalized));
  if (nameExtension && nameExtension.length <= 8 && preferExtension) {
    return nameExtension;
  }

  if (!value || !raw || !normalized) {
    return '';
  }

  if (mimeSubtypeToExtension[normalized]) {
    return mimeSubtypeToExtension[normalized];
  }

  if (normalized.includes('.')) {
    const tail = normalized.split('.').pop();
    if (tail && tail.length <= 8) {
      return tail;
    }
  }

  if (normalized.startsWith('x-') || normalized.includes('-')) {
    const tail = normalized.split('-').pop();
    if (tail && tail.length <= 8) {
      return tail;
    }
  }

  return raw;
};

// DND can yield empty/unknown File.type for RAWs, so infer from extension as a fallback.
const inferMimeType = (file: File): string | undefined => {
  const normalizedType = file.type?.toLowerCase();
  if (normalizedType && normalizedType !== 'application/octet-stream') {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension) {
    return file.type;
  }

  for (const [mime, extensions] of Object.entries(MIME_TYPE_EXTENSIONS)) {
    if (extensions.some((ext) => ext.toLowerCase() === `.${extension}`)) {
      return mime;
    }
  }

  // No mime match for this extension; a guessed value here would be wrong, so leave it unresolved.
  return undefined;
};

/** macOS hides a .DS_Store in every dropped folder and nobody wants it as an asset. */
export const filterRealFiles = <T extends { name: string }>(files: Array<T>) =>
  files.filter((file) => file.name !== '.DS_Store');

export type ExtendedFileWithPath = File & {
  path?: string;
  folderPath: string;
  preview: string;
  id: string;
  sessionId: string;
  inferredType?: string;
};

export type MetadataCsv = {
  filename: string;
  name: string;
  description: string;
  metadata: object;
  tags: Array<string>;
  workflows: object;
  rating: number;
};

export type IngestionAlbum = { id: string; label: string };

export interface UploadSession {
  uploadFolderPath: string;
  selectedAlbums: Array<IngestionAlbum>;
  filesByFolder: Record<string, Array<UploadAsset>>;
  filesData: Array<ExtendedFileWithPath>;
  totalAssetsToUpload: number;
  uploadState: TaskState;
  assetsUploadProgress: Record<string, { progress: number; uploaded: boolean }>;
  erroredAssets: Record<string, string>;
  averageProgress: number;
  errors: Array<string>;
  selectedAssets: Array<{ id: string; name: string; originalName?: string | null; type?: string | null }>;
  uploadStatus: UploadStatus;
  keepExistingFolders: boolean;
  replaceAssetName: boolean;
  isAssetsProcessingCanceled: boolean;
  assetsLoading: boolean;
  csvFileName: string;
  csvData: Record<string, MetadataCsv>;
  manualMetadata: Record<string, MetadataCsv>;
  active: boolean;
  completedUploadCount: number;
  uploadFolderId: string;
  /** Present only for a contributor session; ties every claimed asset back to its submission link. */
  linkId?: string;
  /** Set once a claim 403s for a linkId session — the link expired or the contributor was removed. */
  linkNoLongerAvailable: boolean;
  refetchNode?: () => void;
  isAllSelected: boolean;
  currentSelectedType: NodeType;
  versionUploadAssetId?: string;
  versionUploadVersionId?: string;
  isVersionUpload: boolean;
}

interface UploadStore {
  uploadModalOpen: boolean;
  setUploadModalOpen: (value: boolean) => void;
  uploadQueue: Array<ExtendedFileWithPath>;
  uploading: boolean;
  /** Set when a batch was cut short because authentication ended. See isUploadAuthError. */
  authLostDuringUpload: boolean;
  /**
   * Sessions whose in-flight claim actually failed with isUploadAuthError. Scopes the
   * auth-loss sweep (failInFlightUploads, AuthWall's interruptedFiles) to the batch that
   * was really interrupted, instead of every non-terminal session — a session left
   * Errored by an unrelated per-file failure must not be swept in (DM-2533).
   */
  authLostSessionIds: Array<string>;
  addFilesToQueue: (files: Array<ExtendedFileWithPath>) => void;
  startUpload: (uploadSessionId: string, createAsset: any, queryClient: any) => Promise<void>;

  uploads: Record<string, UploadSession>;
  totalProgress: number;

  createUploadSession: (options?: { setActive?: boolean }) => string;
  removeUploadSession: (uploadSessionId: string) => void;
  setActiveUploadSession: (uploadSessionId: string) => void;
  calculateTotalProgress: () => void;

  setUploadField: <T extends keyof UploadSession>(uploadSessionId: string, key: T, value: UploadSession[T]) => void;
  getUploadSession: (uploadSessionId: string) => UploadSession | undefined;
  getActiveUploadSession: () => { uploadSessionId: string; session: UploadSession };

  setFiles: (uploadSessionId: string, folderPath: string, files: Array<UploadAsset>) => void;
  setFilesData: (uploadSessionId: string, filesData: Array<ExtendedFileWithPath>) => void;
  setFileTrashed: (fileIds: Array<string>, trashed: boolean) => void;
  getTrashedFileIds: (fileIds: Array<string>) => Array<string>;

  setUploadFolderPath: (uploadSessionId: string, value: string) => void;
  setUploadFolderId: (uploadSessionId: string, value: string) => void;

  setSelectedAlbums: (uploadSessionId: string, albums: Array<IngestionAlbum>) => void;
  clearSelectedAlbums: (uploadSessionId: string) => void;

  setTotalAssetsToUpload: (uploadSessionId: string, value: number) => void;
  setUploadState: (uploadSessionId: string, value: TaskState) => void;
  setUploadStatus: (uploadSessionId: string, value: UploadStatus) => void;
  setAssetsProcessingCanceled: (uploadSessionId: string, value: boolean) => void;
  setAssetsLoading: (uploadSessionId: string, value: boolean) => void;
  setLinkNoLongerAvailable: (uploadSessionId: string, value: boolean) => void;
  setCsvFileName: (uploadSessionId: string, value: string) => void;
  setCsvData: (uploadSessionId: string, value: Record<string, MetadataCsv>) => void;
  setManualMetadata: (
    uploadSessionId: string,
    data: Partial<MetadataCsv>,
    assetsData: Array<{ id: string; name: string; originalName?: string | null }>,
    preserveOldData: boolean,
  ) => void;

  changeFolder: (uploadSessionId: string, oldPath: string, newPath: string) => void;
  setAssetsUploadProgress: (uploadSessionId: string, id: string, progress: number, uploaded: boolean) => void;
  removeAssetsUploadProgress: (uploadSessionId: string, id: string) => void;
  setErroredAssets: (uploadSessionId: string, id: string, error: string) => void;
  failInFlightUploads: (message: string) => void;
  setAverageProgress: (uploadSessionId: string, progress?: number) => void;
  clearAllFiles: (uploadSessionId: string, resetKeepExistingFolders?: boolean) => void;
  setErrors: (uploadSessionId: string, errors: Array<string>) => void;
  removeFolder: (uploadSessionId: string, folderPath: string) => void;
  removeAssets: (uploadSessionId: string) => void;
  getFileById: (uploadSessionId: string, fileId: string) => UploadAsset | null;
  toggleKeepExistingFolders: (uploadSessionId: string) => void;
  toggleReplaceAssetName: (uploadSessionId: string) => void;

  batchSize: number;
  uploadRefetchThreshold: number;

  getUploadIcon: (type: string) => LucideIcon;
  getFolderPath: (file: File) => string | undefined;
  redrawThumbnail: (file: File) => Promise<{ file: File; dimensions?: { width: number; height: number } }>;
  onDrop: (acceptedFiles: Array<File>, uploadSessionId: string, createIfMissing?: boolean) => Promise<void>;
  onAssetUploadComplete: (uploadSessionId: string, refetchNode: () => void) => void;

  setUploadProgress: (progress: number, id: string, sessionId: string) => void;
  setOnErrorAsset: (id: string, error: any, sessionId: string) => void;
  updateFileWithRetry: (
    file: { id: string; name: string },
    assetId: string,
    sessionId: string,
    queryClient: any,
    refetchNode?: any,
    delays?: Array<number>,
  ) => void;
}

export const useUploadStore = create<UploadStore>((set, get) => ({
  uploadModalOpen: false,
  setUploadModalOpen: (value) => set({ uploadModalOpen: value }),
  uploadQueue: [],
  uploading: false,
  authLostDuringUpload: false,
  authLostSessionIds: [],
  uploadedFiles: new Set(),

  addFilesToQueue: (files) => {
    set((state) => {
      const existingIds = new Set(state.uploadQueue.map((file) => file.id));
      const newFiles = files.filter((file) => !existingIds.has(file.id));

      if (newFiles.length === 0) {
        return {};
      }

      return { uploadQueue: [...state.uploadQueue, ...newFiles] };
    });
  },
  setUploadProgress: (progress: number, id: string, sessionId: string) => {
    const store = useUploadStore.getState();

    const actualProgress = progress === UploadFinishedPercentage ? UploadFinishingPercentage : progress;
    store.setAssetsUploadProgress(sessionId, id, actualProgress, false);
    store.setAverageProgress(sessionId);
  },
  setOnErrorAsset: (id: string, error: any, sessionId: string) => {
    const store = useUploadStore.getState();
    store.setErroredAssets(sessionId, id, error);
    store.setAssetsUploadProgress(sessionId, id, 100, false);
    store.setUploadState(sessionId, TaskState.Errored);
    store.setAverageProgress(sessionId);
  },
  updateFileWithRetry: (
    file: { id: string; name: string },
    assetId: string,
    sessionId: string,
    queryClient: any,
    refetchNode?: any,
    delays = [2000, 3000, 5000],
  ) => {
    let attempt = 0;
    let isUpdated = false;
    const tryUpdate = () => {
      if (isUpdated) {
        return;
      }
      const currentSession = useUploadStore.getState().uploads[sessionId];
      const sessionFilesByFolder = currentSession?.filesByFolder;
      for (const folder in sessionFilesByFolder) {
        const fileIndex = sessionFilesByFolder[folder].findIndex((f) => f.id === file.id);

        if (fileIndex !== -1) {
          const store = useUploadStore.getState();

          sessionFilesByFolder[folder][fileIndex].id = assetId;
          isUpdated = true;
          store.removeAssetsUploadProgress(sessionId, file.id);
          store.setAssetsUploadProgress(sessionId, assetId, UploadFinishedPercentage, true);
          if (currentSession.selectedAssets[0]?.id === file.id) {
            useUploadStore.setState((state) => ({
              ...state,
              selectedAssets: [{ id: assetId, name: file.name, type: DAMNodeType.Asset }],
            }));
          }
          const newUploadCount = useUploadStore.getState().uploads[sessionId].completedUploadCount + 1;
          store.setUploadField(sessionId, 'completedUploadCount', newUploadCount);
          if (refetchNode && useTreeStore.getState().selectedNode?.node.id === currentSession.uploadFolderId) {
            store.onAssetUploadComplete(sessionId, refetchNode);
          }

          //Last file uploaded for this session
          if (newUploadCount === currentSession.totalAssetsToUpload) {
            if (currentSession.uploadState === TaskState.Cancelled) {
              store.setUploadStatus(sessionId, 'cancelled');
            } else if (Object.keys(currentSession.erroredAssets ?? []).length === 0) {
              store.setUploadStatus(sessionId, 'success');
              store.setUploadState(sessionId, TaskState.Finished);
            } else {
              store.setUploadStatus(sessionId, 'error');
            }
            store.setAverageProgress(sessionId, UploadFinishedPercentage);
            if (refetchNode && useTreeStore.getState().selectedNode?.node.id === currentSession.uploadFolderId) {
              refetchNode();
            }
            queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
          }
          return;
        }
      }

      if (attempt < delays.length) {
        setTimeout(tryUpdate, delays[attempt]);
        attempt++;
      }
    };

    tryUpdate();
  },

  startUpload: async (uploadSessionId: string, createAsset: any, queryClient: any) => {
    const store = useUploadStore.getState();

    const session = store.uploads[uploadSessionId];
    if (!session || session.filesData.length === 0) {
      return;
    }

    store.addFilesToQueue(session.filesData);
    store.setUploadField(uploadSessionId, 'uploadState', TaskState.InProgress);
    if (store.uploading) {
      return;
    }

    // Clear the previous batch's verdict so a stale flag (or a stale session id) cannot
    // suppress a later legitimate redirect or leak into a later batch's notice.
    useUploadStore.setState({ uploading: true, authLostDuringUpload: false, authLostSessionIds: [] });

    try {
      while (true) {
        const currentQueue = useUploadStore.getState().uploadQueue;

        if (currentQueue.length === 0) {
          break;
        }

        const queuedIds = new Set(currentQueue.map((file) => file.id));

        await PromisePool.withConcurrency(5)
          .for(currentQueue)
          .process(async (file) => {
            let currentSession = useUploadStore.getState().uploads[file.sessionId];
            if (currentSession?.uploadState === TaskState.Cancelled) {
              store.setUploadStatus(file.sessionId, 'cancelled');
              return;
            }

            const csvData = currentSession.csvData;
            const manualMetadata = currentSession.manualMetadata;

            const fileKey = file.path
              ? file.path.startsWith('./')
                ? file.path.slice(2)
                : file.path.startsWith('/')
                  ? file.path.slice(1)
                  : file.path
              : null;

            const {
              tags: csvTags = [],
              workflows: csvWorkflows = {},
              name: csvName = '',
              description: csvDescription = '',
              rating: csvRating = 0,
              metadata: csvMetadata = {},
            } = fileKey ? (csvData[capitalizeFileExtension(fileKey)] ?? {}) : {};
            const manualEntry = manualMetadata[file.id] ?? {};

            // metadata (object merge, manual wins)
            const metadata = fileKey
              ? {
                  ...csvMetadata,
                  ...Object.fromEntries(
                    Object.entries(manualEntry.metadata ?? {}).filter(([key]) => key !== 'csvDataKeyRecognition'),
                  ),
                }
              : {};

            // workflows (object merge, manual wins)
            const workflows = {
              ...(csvWorkflows ?? {}),
              ...(manualEntry.workflows ?? {}),
            };

            // tags (array, manual > csv > [])
            const tags = manualEntry.tags ?? csvTags ?? [];

            // description (string, manual > csv > '')
            const description = manualEntry.description ?? csvDescription ?? '';

            // rating (number, manual > csv > 0)
            const rating = Number(manualEntry.rating ?? csvRating ?? 0);

            let result: Awaited<ReturnType<typeof createAsset>> | undefined;
            let rateLimitAttempt = 0;
            while (rateLimitAttempt <= UPLOAD_RATE_LIMIT_MAX_RETRIES) {
              try {
                result = await createAsset({
                  folderId: currentSession.uploadFolderId,
                  file,
                  sessionId: file.sessionId,
                  onProgress: store.setUploadProgress,
                  assetId: currentSession.replaceAssetName ? file.name.replace(/\.[^/.]+$/, '') : undefined,
                  originalFileName: currentSession.keepExistingFolders ? file.path : file.name,
                  temporaryId: file.id,
                  onError: store.setOnErrorAsset,
                  metadata,
                  workflows,
                  tags,
                  description,
                  name: csvName,
                  rating: Number(rating),
                  albumIds: currentSession.selectedAlbums.map((a) => a.id),
                  linkId: currentSession.linkId,
                  suppressGlobalErrorToast: true,
                });
                break;
              } catch (err) {
                if (isUploadAuthError(err)) {
                  useUploadStore.setState((state) => ({
                    authLostDuringUpload: true,
                    authLostSessionIds: state.authLostSessionIds.includes(file.sessionId)
                      ? state.authLostSessionIds
                      : [...state.authLostSessionIds, file.sessionId],
                  }));
                  break;
                }
                if (isUploadLinkDeadError(err)) {
                  store.setLinkNoLongerAvailable(file.sessionId, true);
                  break;
                }
                if (!isUploadRateLimitError(err)) {
                  break;
                }
                rateLimitAttempt++;

                if (rateLimitAttempt > UPLOAD_RATE_LIMIT_MAX_RETRIES) {
                  break;
                }
                const delayMs = UPLOAD_RATE_LIMIT_BASE_DELAY_MS * Math.pow(2, rateLimitAttempt - 1);

                // Cancel-aware wait: exits early if the session is cancelled during backoff.
                const continued = await rateLimitDelay(delayMs, file.sessionId);
                if (!continued) {
                  store.setUploadStatus(file.sessionId, 'cancelled');
                  return;
                }

                // Refresh session snapshot after the potentially multi-minute wait so
                // stale folder/album data is not sent on the next attempt.
                currentSession = useUploadStore.getState().uploads[file.sessionId];
                if (!currentSession || currentSession.uploadState === TaskState.Cancelled) {
                  store.setUploadStatus(file.sessionId, 'cancelled');
                  return;
                }
              }
            }

            if (result?.assetId) {
              store.updateFileWithRetry(file, result.assetId, file.sessionId, queryClient, currentSession.refetchNode);
            } else {
              store.setUploadStatus(file.sessionId, 'error');
              const newUploadCount = useUploadStore.getState().uploads[file.sessionId].completedUploadCount + 1;
              store.setUploadField(file.sessionId, 'completedUploadCount', newUploadCount);

              //Last file uploaded for this session
              if (newUploadCount === currentSession.totalAssetsToUpload) {
                store.setUploadStatus(file.sessionId, 'error');
                store.setAverageProgress(file.sessionId, UploadFinishedPercentage);
                if (
                  currentSession.refetchNode &&
                  useTreeStore.getState().selectedNode?.node.id === currentSession.uploadFolderId
                ) {
                  currentSession.refetchNode();
                }
                queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
                queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
              }
            }
          });

        useUploadStore.setState((state) => ({
          uploadQueue: state.uploadQueue.filter((file) => !queuedIds.has(file.id)),
        }));
      }
    } finally {
      // Refresh on cancel
      if (
        session.refetchNode &&
        useUploadStore.getState().uploads[uploadSessionId]?.uploadState === TaskState.Cancelled &&
        useTreeStore.getState().selectedNode?.node.id === session.uploadFolderId
      ) {
        session.refetchNode();
      }
      useUploadStore.setState({ uploading: false });
    }
  },
  uploads: {},
  totalProgress: 0,

  calculateTotalProgress: () => {
    const uploads = get().uploads;
    const sessions = Object.values(uploads);
    const sessionsToCalculate = sessions.filter((session) => session.uploadState !== TaskState.NotStarted);
    if (sessionsToCalculate.length === 0) {
      set({ totalProgress: 0 });
      return;
    }
    const sum = sessionsToCalculate.reduce((acc, session) => acc + session.averageProgress, 0);
    set({ totalProgress: Math.floor(sum / sessionsToCalculate.length) });
  },

  createUploadSession: (options = {}) => {
    const { setActive = true } = options;
    const state = get();
    const uploads = { ...state.uploads };
    const previousActiveEntry = Object.entries(uploads).find(([_, session]) => session.active);

    if (setActive && previousActiveEntry) {
      const [activeId, activeSession] = previousActiveEntry;
      uploads[activeId] = { ...activeSession, active: false };
    }

    const newSessionId = ulid();
    const shouldBeActive = setActive || !previousActiveEntry;

    uploads[newSessionId] = createEmptyUploadSession(shouldBeActive);

    set({ uploads });
    return newSessionId;
  },

  removeUploadSession: (uploadSessionId) => {
    set((state) => {
      const { [uploadSessionId]: _, ...remaining } = state.uploads;
      return { uploads: remaining };
    });
    get().calculateTotalProgress();
  },

  setActiveUploadSession: (uploadSessionId) => {
    set((state) => {
      if (state.uploads[uploadSessionId]?.active) {
        return state;
      }
      const uploads = { ...state.uploads };
      for (const [id, session] of Object.entries(uploads)) {
        if (session.active) {
          uploads[id] = { ...session, active: false };
        }
      }
      if (uploads[uploadSessionId]) {
        uploads[uploadSessionId] = { ...uploads[uploadSessionId], active: true };
      }
      return { uploads };
    });
  },

  setUploadField: (uploadSessionId, key, value) =>
    set((state) => ({
      uploads: {
        ...state.uploads,
        [uploadSessionId]: {
          ...state.uploads[uploadSessionId],
          [key]: value,
        },
      },
    })),

  getUploadSession: (uploadSessionId) => get().uploads[uploadSessionId],
  getActiveUploadSession: () => {
    /** Removing the active session can leave every remaining session inactive, not zero sessions. */
    const hasActiveSession = Object.values(get().uploads).some((session) => session.active);
    if (!hasActiveSession) {
      get().createUploadSession();
    }
    const entry = Object.entries(get().uploads).find(([, session]) => session.active);
    const [uploadSessionId, session] = entry!;
    return { uploadSessionId, session };
  },
  setFiles: (uploadSessionId, folderPath, files) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'filesByFolder', {
      ...session.filesByFolder,
      [folderPath]: files,
    });
  },
  setFilesData: (uploadSessionId, filesData) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'filesData', filesData);
  },
  // Marks/unmarks files as trashed across every session, so a trash or restore triggered
  // from anywhere (asset details, selection bar, the upload list itself) stays in sync with
  // the upload task list without the two ever going through a second write path.
  // Preserves referential equality for untouched sessions/folders/files so sibling
  // UploadTaskBar subscribers don't re-render (see the selector comment in upload-task-bars.tsx).
  setFileTrashed: (fileIds, trashed) =>
    set((state) => {
      const idSet = new Set(fileIds);
      let uploadsChanged = false;
      const updatedUploads = { ...state.uploads };

      for (const [sessionId, session] of Object.entries(state.uploads)) {
        let sessionChanged = false;
        const updatedFilesByFolder = { ...session.filesByFolder };

        for (const [folder, files] of Object.entries(session.filesByFolder)) {
          let folderChanged = false;
          const updatedFiles = files.map((file) => {
            if (!idSet.has(file.id) || file.trashed === trashed) {
              return file;
            }
            folderChanged = true;
            return { ...file, trashed };
          });

          if (folderChanged) {
            updatedFilesByFolder[folder] = updatedFiles;
            sessionChanged = true;
          }
        }

        if (sessionChanged) {
          updatedUploads[sessionId] = { ...session, filesByFolder: updatedFilesByFolder };
          uploadsChanged = true;
        }
      }

      return uploadsChanged ? { uploads: updatedUploads } : {};
    }),
  // Snapshot helper for callers about to call setFileTrashed(ids, true): tells them which of
  // those ids were already trashed by some other action, so on rollback they only un-hide
  // the ones they newly hid themselves — not ones that must stay hidden regardless of how
  // this particular mutation turns out.
  getTrashedFileIds: (fileIds) => {
    const idSet = new Set(fileIds);
    const trashedIds = new Set<string>();
    for (const session of Object.values(get().uploads)) {
      for (const files of Object.values(session.filesByFolder)) {
        for (const file of files) {
          if (idSet.has(file.id) && file.trashed) {
            trashedIds.add(file.id);
          }
        }
      }
    }
    return Array.from(trashedIds);
  },
  setUploadFolderPath: (uploadSessionId, path) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'uploadFolderPath', path);
  },
  setUploadFolderId: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'uploadFolderId', value);
  },
  setSelectedAlbums: (uploadSessionId, albums) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'selectedAlbums', albums);
  },
  clearSelectedAlbums: (uploadSessionId) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'selectedAlbums', []);
  },

  setTotalAssetsToUpload: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'totalAssetsToUpload', value);
  },
  setUploadState: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'uploadState', value);
  },
  setUploadStatus: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'uploadStatus', value);
  },
  setAssetsProcessingCanceled: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'isAssetsProcessingCanceled', value);
  },
  setLinkNoLongerAvailable: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'linkNoLongerAvailable', value);
  },
  setAssetsLoading: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'assetsLoading', value);
  },
  setCsvFileName: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'csvFileName', value);
  },
  setCsvData: (uploadSessionId, value) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'csvData', value);
  },

  setManualMetadata: (
    uploadSessionId: string,
    data: Partial<MetadataCsv>,
    assetsData: Array<{ id: string; name: string; originalName?: string | null }>,
    preserveOldData: boolean,
  ) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }

    const updatedManualMetadata = { ...session.manualMetadata } as Record<string, any>;

    assetsData.forEach(({ id, name, originalName }) => {
      const prevData = updatedManualMetadata[id] ?? {};
      const csvDataKey = getUploadCsvKey({ name, originalName });

      if (preserveOldData) {
        // MERGE / PATCH mode
        updatedManualMetadata[id] = {
          ...prevData,
          ...data,
          metadata: {
            ...(prevData.metadata ?? {}),
            ...(data.metadata ?? {}),
            // keep recognition fresh in merge mode
            csvDataKeyRecognition: csvDataKey,
          },
        };
      } else {
        // OVERWRITE mode — presence-driven update:
        // start from prev, then apply only fields that exist in `data`
        const next: Record<string, any> = { ...prevData };

        // filename -> stored as fileName (keep your existing mapping)
        if ('filename' in data) {
          next.fileName = data.filename;
        }

        if ('name' in data) {
          if (data.name === undefined) {
            delete next.name;
          } else {
            next.name = data.name;
          }
        }

        if ('description' in data) {
          if (data.description === undefined) {
            delete next.description;
          } else {
            next.description = data.description;
          }
        }

        if ('rating' in data) {
          if (data.rating === undefined) {
            delete next.rating;
          } else {
            next.rating = data.rating;
          }
        }

        if ('tags' in data) {
          if (data.tags === undefined) {
            delete next.tags;
          } else {
            next.tags = data.tags;
          }
        }

        if ('workflows' in data) {
          if (data.workflows === undefined) {
            delete next.workflows;
          } else {
            next.workflows = data.workflows;
          }
        }

        if ('metadata' in data) {
          // replace metadata entirely with what caller computed,
          // but reattach recognition
          next.metadata = {
            ...(data.metadata ?? {}),
            csvDataKeyRecognition: csvDataKey,
          };
        }
        // If metadata wasn't touched, do NOT create/modify it here.

        updatedManualMetadata[id] = next;
      }
    });

    get().setUploadField(uploadSessionId, 'manualMetadata', updatedManualMetadata);
  },
  changeFolder: (uploadSessionId, oldPath, newPath) => {
    const session = get().uploads[uploadSessionId];
    if (!session || oldPath === newPath) {
      return;
    }
    const newFilesByFolder: Record<string, Array<UploadAsset>> = {};

    Object.entries(session.filesByFolder).forEach(([folderPath, files]) => {
      if (folderPath === oldPath || folderPath.startsWith(`${oldPath}/`)) {
        const newFolderPath = folderPath.replace(oldPath, newPath);
        const updatedFiles = files.map((f) => ({ ...f, folderPath: newFolderPath, path: newFolderPath }));
        newFilesByFolder[newFolderPath] = updatedFiles;
      } else {
        newFilesByFolder[folderPath] = files;
      }
    });

    get().setUploadField(uploadSessionId, 'filesByFolder', newFilesByFolder);
  },

  setAssetsUploadProgress: (uploadSessionId, id, progress, uploaded) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'assetsUploadProgress', {
      ...session.assetsUploadProgress,
      [id]: { progress, uploaded },
    });
  },

  removeAssetsUploadProgress: (uploadSessionId, id) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    const { [id]: _, ...rest } = session.assetsUploadProgress;
    get().setUploadField(uploadSessionId, 'assetsUploadProgress', rest);
  },

  setErroredAssets: (uploadSessionId, id, error) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'erroredAssets', {
      ...session.erroredAssets,
      [id]: error,
    });
  },

  // Authentication ended mid-batch. The upload loop cannot continue without it —
  // the claim step sends a bearer token — so drain the queue and report the
  // unfinished files as errored instead of leaving them at partial progress
  // forever. Already-uploaded files keep their state (DM-2533).
  failInFlightUploads: (message) => {
    const store = get();

    // Scoped to the sessions this auth loss actually interrupted — recorded in
    // authLostSessionIds when the claim call fails — plus a fallback for sessions still
    // InProgress, which covers auth flipping while the loop is still running (no claim has
    // failed yet, so no session id was recorded). A session left Errored/InProgress by an
    // unrelated ordinary upload failure is neither recorded nor still InProgress once its
    // failing claim resolves, so it is not swept in (DM-2533).
    const interruptedSessionIds = Object.entries(store.uploads)
      .filter(
        ([sessionId, session]) =>
          session.uploadState !== TaskState.Finished &&
          session.uploadState !== TaskState.Cancelled &&
          session.filesData.some((file) => !isUploadFileSaved(session, file.id)) &&
          (store.authLostSessionIds.includes(sessionId) || session.uploadState === TaskState.InProgress),
      )
      .map(([sessionId]) => sessionId);

    if (interruptedSessionIds.length === 0) {
      return;
    }

    // Promote the InProgress fallback matches into authLostSessionIds too: this same sweep
    // is about to move them to Errored, and once it does they'd no longer match the
    // InProgress fallback on the next render — dropping them out of the notice they
    // themselves are ending up in.
    set((state) => ({
      uploadQueue: [],
      uploading: false,
      authLostSessionIds: Array.from(new Set([...state.authLostSessionIds, ...interruptedSessionIds])),
    }));

    interruptedSessionIds.forEach((sessionId) => {
      const session = get().uploads[sessionId];
      if (!session) {
        return;
      }
      session.filesData.forEach((file) => {
        if (isUploadFileSaved(session, file.id) || session.erroredAssets[file.id]) {
          return;
        }
        get().setErroredAssets(sessionId, file.id, message);
      });
      get().setUploadStatus(sessionId, 'error');
      get().setUploadState(sessionId, TaskState.Errored);
    });
  },

  setAverageProgress: (uploadSessionId, progress) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    if (progress) {
      get().setUploadField(uploadSessionId, 'averageProgress', progress);
      get().calculateTotalProgress();
      return;
    }
    const values = Object.values(session.assetsUploadProgress).map((e) => e.progress);
    const average = values.length ? Math.floor(values.reduce((a, b) => a + b, 0) / session.totalAssetsToUpload) : 100;
    get().setUploadField(uploadSessionId, 'averageProgress', average);
    get().calculateTotalProgress();
  },

  clearAllFiles: (uploadSessionId, resetKeepExistingFolders = true) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }

    // Revoke object URLs for previews
    session.filesData.forEach((file) => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });

    for (const assets of Object.values(session.filesByFolder)) {
      for (const asset of assets) {
        const url = asset?.variants?.[Variants.ThumbnailInternal]?.url;
        if (url) {
          URL.revokeObjectURL(url);
        }
      }
    }
    session.filesData.length = 0;
    get().setUploadField(uploadSessionId, 'isAssetsProcessingCanceled', true);
    get().setUploadField(uploadSessionId, 'filesByFolder', {});
    get().setUploadField(uploadSessionId, 'filesData', []);
    get().setUploadField(uploadSessionId, 'assetsUploadProgress', {});
    get().setUploadField(uploadSessionId, 'uploadState', TaskState.NotStarted);
    get().setUploadField(uploadSessionId, 'averageProgress', 0);
    get().setUploadField(uploadSessionId, 'totalAssetsToUpload', 0);
    get().setUploadField(uploadSessionId, 'erroredAssets', {});
    get().setUploadField(uploadSessionId, 'uploadStatus', 'ready');
    get().setUploadField(uploadSessionId, 'selectedAssets', []);
    get().setUploadField(uploadSessionId, 'keepExistingFolders', resetKeepExistingFolders);
    get().setUploadField(uploadSessionId, 'replaceAssetName', false);
    get().setUploadField(uploadSessionId, 'assetsLoading', false);
    get().setUploadField(uploadSessionId, 'csvFileName', '');
    get().setUploadField(uploadSessionId, 'csvData', {});
    get().setUploadField(uploadSessionId, 'completedUploadCount', 0);
    get().setUploadField(uploadSessionId, 'manualMetadata', {});
    get().setUploadField(uploadSessionId, 'refetchNode', undefined);
    get().setUploadField(uploadSessionId, 'isAllSelected', false);
    get().setUploadField(uploadSessionId, 'currentSelectedType', NodeType.Folders);
    get().setUploadField(uploadSessionId, 'isVersionUpload', false);
  },

  setErrors: (uploadSessionId, errors) => {
    get().setUploadField(uploadSessionId, 'errors', errors);
  },

  removeFolder: (uploadSessionId, folderPath) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }

    const match = session.keepExistingFolders
      ? (path: string) => path === folderPath
      : (path: string) => path.startsWith(folderPath);

    const newFilesByFolder = Object.fromEntries(Object.entries(session.filesByFolder).filter(([key]) => !match(key)));

    const removedFileNames = session.filesData
      .filter((file) => match(file.folderPath))
      .map((file) => capitalizeFileExtension(file.name));

    const newFilesData = session.filesData.filter((file) => !match(file.folderPath));

    const newSelected = session.selectedAssets.filter(
      (item) => !session.filesData.some((file) => match(file.folderPath) && file.id === item.id),
    );

    const newManualMetadata = Object.fromEntries(
      Object.entries(session.manualMetadata || {}).filter(([fileName]) => !removedFileNames.includes(fileName)),
    );

    get().setUploadField(uploadSessionId, 'filesByFolder', newFilesByFolder);
    get().setUploadField(uploadSessionId, 'filesData', newFilesData);
    get().setUploadField(uploadSessionId, 'selectedAssets', newSelected);
    get().setUploadField(uploadSessionId, 'totalAssetsToUpload', newFilesData.length);
    get().setUploadField(uploadSessionId, 'manualMetadata', newManualMetadata);
  },

  removeAssets: (uploadSessionId) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }

    const allSelected = session.filesData.length === session.selectedAssets.length;

    const newFilesByFolder = Object.entries(session.filesByFolder).reduce(
      (acc, [folder, files]) => {
        const remaining = files.filter((f) => !session.selectedAssets.some((s) => s.id === f.id));
        if (remaining.length > 0) {
          acc[folder] = remaining;
        }
        return acc;
      },
      {} as Record<string, Array<UploadAsset>>,
    );

    const newFilesData = session.filesData.filter((file) => !session.selectedAssets.some((s) => s.id === file.id));

    const updatedManualMetadata = Object.fromEntries(
      Object.entries(session.manualMetadata || {}).filter(
        ([fileName]) => !session.selectedAssets.some((s) => capitalizeFileExtension(s.name) === fileName),
      ),
    );

    get().setUploadField(uploadSessionId, 'filesByFolder', newFilesByFolder);
    get().setUploadField(uploadSessionId, 'filesData', newFilesData);
    get().setUploadField(uploadSessionId, 'selectedAssets', []);
    get().setUploadField(uploadSessionId, 'totalAssetsToUpload', newFilesData.length);
    get().setUploadField(uploadSessionId, 'manualMetadata', updatedManualMetadata);
    if (allSelected) {
      get().setUploadField(uploadSessionId, 'isAllSelected', false);
    }
  },

  getFileById: (uploadSessionId, fileId) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return null;
    }
    for (const folder in session.filesByFolder) {
      const found = session.filesByFolder[folder].find((f) => f.id === fileId);
      if (found) {
        return found;
      }
    }
    return null;
  },

  toggleKeepExistingFolders: (uploadSessionId) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'keepExistingFolders', !session.keepExistingFolders);
  },

  toggleReplaceAssetName: (uploadSessionId) => {
    const session = get().uploads[uploadSessionId];
    if (!session) {
      return;
    }
    get().setUploadField(uploadSessionId, 'replaceAssetName', !session.replaceAssetName);
  },

  batchSize: 20,
  uploadRefetchThreshold: 10,
  getUploadIcon: (type) => {
    if (type.startsWith('image/')) {
      return ImageIcon;
    }
    if (type.startsWith('video/')) {
      return VideoIcon;
    }
    return FileIcon;
  },

  getFolderPath: (file) => {
    const path = (file as FileWithPath)?.path;
    return path?.substring(0, path.lastIndexOf('/'));
  },
  redrawThumbnail: async (file: File): Promise<{ file: File; dimensions?: { width: number; height: number } }> => {
    const result: { file: File; dimensions?: { width: number; height: number } } = { file };

    if (file.type.startsWith('image/') && !['image/tiff', 'image/tif'].includes(file.type)) {
      try {
        const bitmap = await createImageBitmap(file);
        result.dimensions = { width: bitmap.width, height: bitmap.height };

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return result;
        }

        const maxWidth = 150;
        const maxHeight = 150;
        const aspect = bitmap.width / bitmap.height;
        let width = maxWidth,
          height = maxHeight;

        if (aspect > 1) {
          height = maxWidth / aspect;
        } else {
          width = maxHeight * aspect;
        }

        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), file.type, 0.5));

        if (blob) {
          result.file = new File([blob], file.name, { type: file.type });
        }
      } catch {
        // Could not create bitmap or compress so we just return original file.
      }
    }

    return result;
  },

  onDrop: async (droppedFiles, uploadSessionId) => {
    const store = get();
    const getSession = () => get().uploads[uploadSessionId];
    if (!getSession()) {
      return;
    }

    const acceptedFiles = filterRealFiles(droppedFiles);

    // Reset processing state
    store.setUploadField(uploadSessionId, 'isAssetsProcessingCanceled', false);
    store.setUploadField(uploadSessionId, 'assetsLoading', true);

    const totalExistingFiles = getSession()?.filesData.length ?? 0;
    store.setUploadField(uploadSessionId, 'totalAssetsToUpload', acceptedFiles.length + totalExistingFiles);

    // Map files to full metadata in drop order
    // Precompute inferredType for each file so UI can show it immediately
    const allNewFiles: Array<
      File & {
        preview: string;
        folderPath: string;
        id: string;
        sessionId: string;
        inferredType: string | undefined;
      }
    > = acceptedFiles.map((file) => {
      const folderPath = store.getFolderPath(file) ?? '';
      const uploadFolderPath = getSession()?.uploadFolderPath ?? '';
      const fullFolderPath =
        folderPath === '.' || folderPath === '' ? uploadFolderPath : joinFolderPath(uploadFolderPath, folderPath);
      const inferredType = inferMimeType(file);

      return Object.assign(file, {
        preview: URL.createObjectURL(file),
        folderPath: fullFolderPath,
        id: `${ulid()}-temp`,
        sessionId: uploadSessionId,
        inferredType,
      });
    });

    // Update filesData in drag order
    const existingFiles = getSession()?.filesData ?? [];
    store.setUploadField(uploadSessionId, 'filesData', [...existingFiles, ...allNewFiles]);

    for (let i = 0; i < allNewFiles.length; i += store.batchSize) {
      if (getSession()?.isAssetsProcessingCanceled) {
        break;
      }

      const batch = allNewFiles.slice(i, i + store.batchSize);

      let compressedFiles: Array<{
        file: File;
        dimensions?: { width: number; height: number };
      }>;
      try {
        compressedFiles = await Promise.all(batch.map(store.redrawThumbnail));
      } catch (error) {
        errorLog('Compression error:', error);
        continue;
      }

      if (getSession()?.isAssetsProcessingCanceled) {
        break;
      }

      const batchAssets = compressedFiles.map(({ file: compressedFile, dimensions }, index) => {
        const originalFile = batch[index] as File & {
          folderPath: string;
          id: string;
          inferredType?: string;
        };
        const resolvedType = originalFile.inferredType ?? originalFile.type;

        const lastDot = originalFile.name.lastIndexOf('.');
        return {
          id: originalFile.id,
          name: lastDot > 0 ? originalFile.name.slice(0, lastDot) : originalFile.name,
          originalName: originalFile.name,
          type: resolvedType,
          path: originalFile.folderPath,
          folderPath: originalFile.folderPath,
          fileMetadata: {},
          size: originalFile.size,
          nodeId: '',
          slug: originalFile.name.replace(/\s+/g, '-').toLowerCase(),
          description: '',
          trashed: false,
          width: dimensions?.width,
          height: dimensions?.height,
          variants: {
            [Variants.ThumbnailInternal]: {
              width: 150,
              height: 150,
              url: URL.createObjectURL(compressedFile),
              type: resolvedType,
              name: originalFile.name,
            },
          },
        };
      });

      // Immediately update filesByFolder for this batch
      for (const asset of batchAssets) {
        const folder = asset.folderPath;
        const current = getSession()?.filesByFolder[folder] ?? [];
        store.setFiles(uploadSessionId, folder, [...current, asset]);
      }
    }

    // Mark as done
    if (!getSession()?.isAssetsProcessingCanceled) {
      store.setUploadField(uploadSessionId, 'assetsLoading', false);
    }
  },
  onAssetUploadComplete: (uploadSessionId, refetchNode) => {
    set((state) => {
      const session = state.uploads[uploadSessionId];
      if (!session) {
        return {};
      }
      if (session.completedUploadCount % state.uploadRefetchThreshold === 0) {
        throttledRefetch(refetchNode);
      }
      return {};
    });
  },
}));
